import { Box, Checkbox, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { headCellsOrders } from './HeadCells/orders'
import { useSelector } from '../../../../store/store'
import { isOrderColumnVisible, OrderColumnId } from '../orderColumns'

export type Order = 'asc' | 'desc'
interface EnhancedTableProps {
  checkbox: boolean
  numSelected: number
  onRequestSort: (event: React.MouseEvent<unknown>, property) => void
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  order: Order
  orderBy: string
  rowCount: number
}

const EnhancedTableHead = (props: EnhancedTableProps) => {
  const { checkbox, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props
  const createSortHandler = (property) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const { permissions, userInfo } = useSelector((state) => state.user)
  const { role } = userInfo

  // Derived per render from the current permissions, so switching user or
  // navigating never leaves a previously filtered list behind.
  const visibleHeadCells = useMemo(
    () =>
      headCellsOrders.filter((headCell) =>
        isOrderColumnVisible(headCell.id as OrderColumnId, permissions?.orderDetailsFields, role)
      ),
    [permissions?.orderDetailsFields, role]
  )

  return (
    <TableHead>
      <TableRow>
        {checkbox && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'select all desserts',
              }}
            />
          </TableCell>
        )}
        {visibleHeadCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{ whiteSpace: 'pre' }}
            >
              <FormattedMessage id={headCell.label} />
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell />
      </TableRow>
    </TableHead>
  )
}

export default EnhancedTableHead
